import { supabase } from '../../../lib/supabase'
import { DuplicateGroup, ProgressStore, ExportRecord } from '../types'

function parseCSVLine(line: string): string[] {
  const result: string[] = []
  let current = ''
  let inQuotes = false

  for (let i = 0; i < line.length; i++) {
    const char = line[i]
    if (char === '"') {
      inQuotes = !inQuotes
    } else if (char === ',' && !inQuotes) {
      result.push(current)
      current = ''
    } else {
      current += char
    }
  }
  result.push(current)
  return result
}

function escapeCSVField(value: string): string {
  if (value.includes(',') || value.includes('"') || value.includes('\n')) {
    return '"' + value.replace(/"/g, '""') + '"'
  }
  return value
}

export async function exportMergedCompanies(
  progress: ProgressStore,
  groups: Map<string, DuplicateGroup>
): Promise<{ newCount: number; removed: number }> {
  // Fetch the full Companies CSV (lazy — only on export)
  const response = await fetch('/merge-review/Companies.csv')
  const csvText = await response.text()

  const lines = csvText.trim().split('\n')
  if (lines.length < 2) throw new Error('Companies CSV is empty')

  const originalCount = lines.length - 1
  const headers = parseCSVLine(lines[0])

  // Determine which line numbers to remove and which to replace
  const linesToRemove = new Set<number>()
  const replacements = new Map<number, Record<string, string>>()
  let mergedGroups = 0
  let skippedGroups = 0
  let notDuplicateGroups = 0

  for (const [gid, decision] of Object.entries(progress)) {
    if (decision.action === 'merged') {
      mergedGroups++
      const group = groups.get(gid)
      if (!group) continue

      const lineNumbers = group.records.map(r => r.lineNumber)
      const keep = lineNumbers[0]
      for (let i = 1; i < lineNumbers.length; i++) {
        linesToRemove.add(lineNumbers[i])
      }
      if (decision.mergedValues) {
        replacements.set(keep, decision.mergedValues)
      }
    } else if (decision.action === 'skipped') {
      skippedGroups++
    } else if (decision.action === 'not_duplicates') {
      notDuplicateGroups++
    }
  }

  // Build output rows
  const outputRows: string[][] = []
  for (let i = 1; i < lines.length; i++) {
    const lineNumber = i + 1 // Companies.csv line numbers start at 2 for first data row
    if (linesToRemove.has(lineNumber)) continue

    const values = parseCSVLine(lines[i])
    const row: Record<string, string> = {}
    headers.forEach((h, idx) => {
      row[h] = values[idx] || ''
    })

    if (replacements.has(lineNumber)) {
      const mergedVals = replacements.get(lineNumber)!
      for (const [k, v] of Object.entries(mergedVals)) {
        if (k in row) {
          row[k] = v
        }
      }
    }

    outputRows.push(headers.map(h => row[h]))
  }

  // Generate CSV content
  const csvLines = [
    headers.map(escapeCSVField).join(','),
    ...outputRows.map(row => row.map(escapeCSVField).join(',')),
  ]
  const csvContent = csvLines.join('\n')
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })

  // Upload to Supabase Storage
  const timestamp = new Date().toISOString().replace(/[:.]/g, '-')
  const filePath = `Companies_merged_${timestamp}.csv`

  const { error: uploadError } = await supabase.storage
    .from('merge-exports')
    .upload(filePath, blob, { contentType: 'text/csv', upsert: false })

  if (uploadError) {
    console.error('Failed to upload export to storage:', uploadError)
    throw new Error('Failed to save export file')
  }

  // Record export metadata
  const { data: userData } = await supabase.auth.getUser()
  await supabase.from('merge_export_history').insert({
    file_path: filePath,
    original_count: originalCount,
    new_count: outputRows.length,
    removed_count: linesToRemove.size,
    merged_groups: mergedGroups,
    skipped_groups: skippedGroups,
    not_duplicate_groups: notDuplicateGroups,
    exported_by: userData?.user?.id || null,
  })

  // Also trigger a browser download for immediate use
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = `Companies_merged_${timestamp}.csv`
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  URL.revokeObjectURL(url)

  return {
    newCount: outputRows.length,
    removed: linesToRemove.size,
  }
}

export async function getExportHistory(): Promise<ExportRecord[]> {
  const { data, error } = await supabase
    .from('merge_export_history')
    .select('*')
    .order('exported_at', { ascending: false })

  if (error) {
    console.error('Error fetching export history:', error)
    return []
  }

  return (data || []).map(row => ({
    id: row.id,
    filePath: row.file_path,
    originalCount: row.original_count,
    newCount: row.new_count,
    removedCount: row.removed_count,
    mergedGroups: row.merged_groups,
    skippedGroups: row.skipped_groups,
    notDuplicateGroups: row.not_duplicate_groups,
    exportedBy: row.exported_by,
    exportedAt: row.exported_at,
  }))
}

export async function downloadExport(filePath: string): Promise<void> {
  const { data, error } = await supabase.storage
    .from('merge-exports')
    .download(filePath)

  if (error || !data) {
    console.error('Failed to download export:', error)
    throw new Error('Failed to download export file')
  }

  const url = URL.createObjectURL(data)
  const link = document.createElement('a')
  link.href = url
  link.download = filePath
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  URL.revokeObjectURL(url)
}
